import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import { Col, FormGroup, Label } from "reactstrap";
import { GallaeryImage } from "types/campaign/creatives";
import { addCampaignGalleryImage, deleteCampaignGalleryImage } from "services/api/campaign";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "store";
import { updateGallery } from "slices/campaigns/creatives/reducer";
import GalleryIcon from "assets/icons/GalleryIcon";

interface CreativesProps {
    campaignId: string,
    campaignHeroImage: string | null;
    gallery: GallaeryImage[];
    onHeroImageChange: (image: File) => void
}

const Creatives: React.FC<CreativesProps> = ({ campaignId, gallery, campaignHeroImage, onHeroImageChange }) => {
    const selectedAgency = useSelector((state: RootState) => state.auth.selectedAgency);
    const [galleryImages, setGalleryImages] = useState<GallaeryImage[]>(gallery);
    const [heroImage, setHeroImage] = useState<string | null>(campaignHeroImage);

    const [newImages, setNewImages] = useState<File[]>([]);

    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [isDragging, setIsDragging] = useState(false);

    const dispatch: AppDispatch = useDispatch();

    useEffect(() => {
        if (selectedFile) {
            onHeroImageChange(selectedFile);
        }
    }, [selectedFile])

    // Set images to local states
    useEffect(() => {
        setGalleryImages(gallery);
        setHeroImage(campaignHeroImage);
    }, [gallery, campaignHeroImage])

    // Open file picker when clicking edit button
    const handleEditClick = () => {
        fileInputRef.current?.click();
    };

    // Handle file selection
    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setSelectedFile(file);
        }
    };

    // Drag & Drop Handlers for Hero Image
    const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = () => {
        setIsDragging(false);
    };

    const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setIsDragging(false);
        const file = event.dataTransfer.files?.[0];
        if (file) {
            setSelectedFile(file);
        }
    };

    // Dropzone Logic for Gallery Images
    const onDrop = useCallback(async (acceptedFiles: File[]) => {
        for (let file of acceptedFiles) {
            const formData = new FormData();
            formData.append('file', file);
            try {
                const response = await addCampaignGalleryImage(selectedAgency?.uuid!, campaignId, formData);
                if (response.success) {
                    const renamedFile = new File([file], response.success, { type: file.type });
                    setNewImages((prev) => [...prev, renamedFile]);
                }
            } catch (error) {
                console.log(error);
            }
        }

    }, []);

    const handleDeleteExisting = async (fileId: string) => {
        try {
            const response = await deleteCampaignGalleryImage(selectedAgency?.uuid!, campaignId, fileId);
            if (response) {
                let updatedFiles = galleryImages.filter((img) => img.uuid !== fileId);
                dispatch(updateGallery({ campaignId, gallery: updatedFiles }));
            }
        } catch (error) {
            console.log(error);
        }
    };

    const handleDeleteNew = async (file: File) => {
        try {
            const response = await deleteCampaignGalleryImage(selectedAgency?.uuid!, campaignId, file.name);
            if (response) {
                setNewImages((prev) => prev.filter((img) => img.name !== file.name));
            }
        } catch (error) {
            console.log(error);
        }
    };

    // Dropzone Configuration
    const { getRootProps, getInputProps } = useDropzone({
        onDrop,
        accept: { ".png, .jpg, .jpeg": [] },
        multiple: true,
    });

    return (
        <React.Fragment>
            {/* Hero Image Section */}
            <FormGroup row className="py-4 mt-3">
                <Col sm={4} className="my-1">
                    <div className="d-flex align-items-center">
                        <span className="icon-circle me-3"><GalleryIcon /></span>
                        <Label htmlFor="name" className="form-label input-label mb-0">
                            Hero Image
                        </Label>
                    </div>
                </Col>
                <Col sm={8} className="my-1">
                    <div
                        className={`${heroImage ? 'campaign-creative-img' : 'campaign-creative-img-placeholder'} ${isDragging ? "dragging" : ""}`}
                        style={{
                            backgroundImage: `url(${selectedFile ? URL.createObjectURL(selectedFile) : heroImage})`,
                        }}
                        onClick={handleEditClick}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                    >
                        {heroImage ? (
                            <div className="creative-action">
                                <i className="bx bxs-pencil"></i>
                            </div>
                        ) : (
                            <button type="button" className="creative-add-btn">+</button>
                        )}
                    </div>
                    <input
                        type="file"
                        ref={fileInputRef}
                        style={{ display: "none" }}
                        accept=".png, .jpg, .jpeg"
                        onChange={handleFileChange}
                    />
                </Col>
            </FormGroup>

            <div className="divider"></div>

            {/* Gallery Images Section */}
            <FormGroup row className="py-4 mt-3">
                <Col sm={4} className="my-1">
                    <div className="d-flex align-items-center">
                        <span className="icon-circle me-3"><GalleryIcon /></span>
                        <Label className="form-label input-label mb-0">
                            Gallery Images
                        </Label>
                    </div>
                </Col>
                <Col sm={8} className="my-1">
                    {/* Entire gallery-container is a Dropzone */}
                    <div {...getRootProps()} className="gallery-container">
                        <input {...getInputProps()} />

                        {/* Clickable Placeholder for File Explorer */}
                        <div
                            className="campaign-creative-img-placeholder"
                        >
                            <button type='button' className="creative-add-btn">+</button>
                        </div>

                        {/* Existing API Images */}
                        {galleryImages.length > 0 &&
                            galleryImages.map((image, index) => (
                                <div key={image.uuid} className="campaign-creative-img" onClick={(e) => e.stopPropagation()} style={{ backgroundImage: `url(${image.path})` }}>
                                    <button type='button' className="creative-delete-btn" onClick={() => handleDeleteExisting(image.uuid)}>
                                        <i className="bx bxs-trash-alt"></i>
                                    </button>
                                </div>
                            ))}

                        {/* Newly Uploaded Images */}
                        {newImages.length > 0 &&
                            newImages.map((file, index) => (
                                <div key={index} className="campaign-creative-img" onClick={(e) => e.stopPropagation()} style={{ backgroundImage: `url(${URL.createObjectURL(file)})` }}>
                                    <button type='button' className="creative-delete-btn" onClick={() => handleDeleteNew(file)}>
                                        <i className="bx bxs-trash-alt"></i>
                                    </button>
                                </div>
                            ))}
                    </div>
                </Col>
            </FormGroup>
        </React.Fragment>
    );
};

export default Creatives;